import { MAX_STORED_SLOT_VALUE } from '~/models/komaValueLimit'

/** Supply48 の入力文字列を数値として解釈するための局所正規化。 */
function toHalfWidth(raw: string): string {
  return raw
    .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/[，]/g, ',')
    .replace(/[．]/g, '.')
    .replace(/[－ー−―]/g, '-')
}

/**
 * セル入力を採用できなかった理由 (AC-S4D-028 / AC-S4D-041)。
 *
 * 旧実装は「採用しない値」をすべて null に潰していたため、呼び出し側は
 * 値を黙って据え置くしかなかった (= AC-S4D-041 が禁止する silent 破棄)。
 * 理由を型で持ち回すことで「なぜ保存できないか」を必ず表示できる状態にする。
 */
export type CellValueRejection =
  | 'decimal'
  | 'negative'
  | 'notNumber'
  | 'oddInKw'
  | 'overDigitLimit'

/**
 * 保存時チェック (`checkSavableStoredValue`) が返しうる理由だけの狭い型
 * (AC-S4D-028 / AC-S4D-047)。`decimal` / `negative` / `notNumber` は入力段階
 * (`parseCellValue`) でしか出ないため、 保存拒否セルのエラー印 (AC-S4D-049の
 * `saveErrorTooltipKey`・`collectSaveErrorCells`) はこちらの型を使う
 * (`CellValueRejection` 全体を使うと、 実際には出ない理由の分だけ i18n キーが
 * 死蔵する)。
 */
export type SaveErrorReason = Extract<
  CellValueRejection,
  'oddInKw' | 'overDigitLimit'
>

export type CellValueParseResult =
  | { ok: false; reason: CellValueRejection }
  | { ok: true; value: number }

/**
 * セル入力値 (手入力 / クリップボード) を **表示単位の**非負整数として解釈する。
 *
 * 全角正規化と桁区切りカンマの除去を行い、採用できない値は理由付きで返す。
 * 空文字は呼び出し側が「クリア」として扱うため、ここへは渡さないこと。
 *
 * kW モードの偶数制約 (AC-S4D-028) はここでは見ない。1 打鍵ごとに偶数判定すると
 * 「102」の途中経過「1」「10」の "1" が弾かれて入力自体が壊れるため、
 * AC の文言どおり**保存時**に checkSavableStoredValue で判定する。
 */
export function parseCellValue(raw: string): CellValueParseResult {
  const trimmed = toHalfWidth(raw).replace(/,/g, '').trim()

  // 非負整数のみ受け付ける。"12abc" のような部分一致を弾くため
  // parseInt (前方一致で通る) ではなく形の検証を先に置く。
  if (!/^\d+$/.test(trimmed)) {
    if (/^-?\d*\.\d+$/.test(trimmed)) return { ok: false, reason: 'decimal' }
    if (/^-\d+$/.test(trimmed)) return { ok: false, reason: 'negative' }
    return { ok: false, reason: 'notNumber' }
  }

  return { ok: true, value: Number.parseInt(trimmed, 10) }
}

/**
 * 表示単位の値をセルの表示文字列へ整形する (spec §6「画面表示は 3 桁区切りカンマ付き」)。
 *
 * セルは入力欄そのものなので、整形結果は再び parseCellValue で読める形に限る
 * (カンマは parseCellValue が除去する)。null は空セル。
 *
 * 呼び出し元 (GridCell) が渡す null は編集中の明示クリアのみを指す。サーバー値が
 * 未設定のコマは `displaySlotValue` (gridRows.ts) が既に 0 へ fallback している
 * ため、null を空セルにするこの関数の既定動作がその null→0 方針 (AC-S4D-050 /
 * AC-S4D-053 ※) を上書きすることはない。
 */
export function formatCellValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return value.toLocaleString('en-US')
}

/**
 * 保存単位 (kWh/30min) の値が保存可能かを判定する (AC-S4D-028 / AC-S4D-047)。
 *
 * - kW 表示で奇数を入力すると kWh/30min 換算で 0.5 単位になり、広域機関提出値・
 *   通告値・HJKS 連携値の整数前提を壊す (AC-S4D-028)。
 * - 換算後の値が OCCTO 提出値の桁数上限 (9 桁 = 999,999,999) を超える場合も
 *   保存を拒否する (AC-S4D-047)。整数チェックが優先する（spec §機能詳細
 *   バリデーション NOTE「整数チェックに違反がある場合、他のバリデーションは
 *   整数エラー修正後に再評価する」）ため非整数は先に oddInKw で拒否する。
 *
 * 表示単位のまま弾くと途中経過の打鍵が壊れるため、保存の直前にここで確定値
 * だけを見る (AC-S4D-028 と同じ理由で AC-S4D-047 もここで判定する)。
 */
export function checkSavableStoredValue(
  stored: number | null,
): SaveErrorReason | null {
  if (stored === null) return null
  if (!Number.isInteger(stored)) return 'oddInKw'
  if (stored > MAX_STORED_SLOT_VALUE) return 'overDigitLimit'
  return null
}

/** グリッド座標付きの拒否情報 (キーボード hook が座標だけを知る形で報告する) */
export type RawCellRejection = {
  reason: CellValueRejection
  /** 入力された生値 (エラー文言にそのまま出す) */
  raw: string
  row: number
  col: number
}

/** 表示ラベルまで解決した拒否情報 (グリッドが座標をラベルへ写して作る) */
export type CellInputRejection = {
  reason: CellValueRejection
  raw: string
  /** 項目名 = 供給源の列見出し (取引先名) */
  itemName: string
  dateLabel: string
  komaLabel: string
}

/**
 * 1 操作分の入力拒否レポート。
 *
 * 手入力・一括入力はセルごとに理由を出す (AC-S4D-041) が、貼付は AC-S4D-042 が
 * 「反映できなかったセルが {件数} 件あります（数値以外が入力されたため）」という
 * 件数ベースの通知を正文として定めているため、経路で形を分ける。
 * 拒否ゼロの操作は `cells` + 空配列で報告する (= 直前のエラー表示の解除)。
 */
export type RawRejectionReport =
  | { kind: 'cells'; rejections: RawCellRejection[] }
  | { kind: 'pasteSkipped'; count: number }

/** 表示ラベルまで解決した拒否レポート (グリッド → ページ) */
export type CellInputRejectionReport =
  | { kind: 'cells'; rejections: CellInputRejection[] }
  | { kind: 'pasteSkipped'; count: number }

/** 拒否なし (= 直前のエラー表示を解除する) */
export const NO_REJECTIONS: RawRejectionReport = {
  kind: 'cells',
  rejections: [],
}

/**
 * 拒否理由 → i18n キー。文言の正本は AC-S4D-028 (小数) と
 * AC-S4D-041 (理由を明示し silent 破棄しない)。
 */
export function rejectionMessageKey(reason: CellValueRejection): string {
  return `page:supply48Data.inputRejected.${reason}`
}

/**
 * セル単位のエラーマーク (AC-S4D-049、design `cell-save-error` の
 * `data-tooltip`) に出す短縮理由 → i18n キー。
 *
 * `inputRejected.*` (バナー文言) は項目名・日付・コマを含む文が正本だが、
 * セル自体に貼る tooltip はそのセル上に既に表示されているため、同じ情報を
 * 繰り返さない短縮文言を別キーで持つ (design 48data.html:3767
 * `'保存できません：' + iss.msg` と同じ「保存できません：+理由」の形)。
 */
export function saveErrorTooltipKey(reason: SaveErrorReason): string {
  return `page:supply48Data.saveErrorTooltip.${reason}`
}
